// Onboarding step 4, "Connect your site" (2026-09-28, for every account).
//
// Self-serve onboarding (spec in Cloudflare-Worker, https://github.com/Digital-Mozarts/Cloudflare-Worker,
// branch feature/self-serve-onboarding): the install guide checks the customer's landing page as
// the step opens (a site scan) and says what to add and where; Test Connection then opens that
// page and waits for the SDK's beacon, the same check Settings → Setup runs. It also asks for
// diagnostics (Phase 3): the popup stays open while the SDK tests how the page treats a change,
// and the recommendation below reads the results. Continue needs a passing test (snippet inside
// <head>); Skip for now is always there. The step is saved here, and onDone tells the wizard to
// move on.
import React, { useState } from 'react';
import axios from 'axios';
import InstallGuidePanel from './InstallGuidePanel';
import SiteExtensionsPanel from './SiteExtensionsPanel';
import { verifySdkPopup, describeVerifyFailure } from '../utils/verifySdkPopup';

const PRIMARY = '#132848';
const START_ERROR = 'Could not start the test. Please check the address and try again.';
const TESTING = 'Snippet found. Now testing how your page handles changes: keep the new tab open.';
const SAVE_ERROR = 'Could not save this step. Please try again.';

// The install guide's snippet always carries the key as data-cognitive-ux-site="…".
const clientKeyOf = (snippet) => ((snippet || '').match(/data-cognitive-ux-site="([^"]+)"/) || [])[1] || '';

const ConnectSiteStep = ({ app_url, defaultUrl = '', onDone }) => {
    const [embedHtml, setEmbedHtml] = useState('');
    const [url, setUrl] = useState(defaultUrl);
    const [testing, setTesting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [passed, setPassed] = useState(false);
    const [message, setMessage] = useState(null);

    const testConnection = async () => {
        setPassed(false);
        const page = url.trim();
        if (!page) {
            setMessage({ ok: false, text: 'Enter the address of the page you added the snippet to.' });
            return;
        }
        setMessage(null);
        setTesting(true);
        try {
            const { data } = await axios.post(`${app_url}/api/store/check-sdk-script`,
                { url: page, clientKey: clientKeyOf(embedHtml), diagnostics: true }, { withCredentials: true });
            if (!data || data.mode !== 'popup' || !data.popupUrl || !data.pollUrl) {
                setMessage({ ok: false, text: (data && data.message) || START_ERROR });
                return;
            }
            const result = await verifySdkPopup({
                popupUrl: data.popupUrl,
                pollUrl: data.pollUrl,
                token: data.token,
                expiresInMs: data.expiresInMs,
                waitForDiagnostics: true,
                onVerified: () => setMessage({ ok: true, text: TESTING }),
            });
            if (result.ok && result.inHead) {
                setPassed(true);
                setMessage({ ok: true, text: "Connected. The snippet is in your page's <head>." });
            } else if (result.ok) {
                setMessage({ ok: false, text: 'The snippet loads, but not inside <head>. Move it to the top of <head> and test again.' });
            } else {
                setMessage({ ok: false, text: describeVerifyFailure(result) });
            }
        } catch (err) {
            setMessage({ ok: false, text: (err && err.response && err.response.data && err.response.data.message) || START_ERROR });
        } finally {
            setTesting(false);
        }
    };

    const save = async (connected) => {
        const step = { connected, skipped: !connected };
        setSaving(true);
        try {
            await axios.post(`${app_url}/api/account-setup/step4`, step, { withCredentials: true });
            // Stays busy: the wizard leaves the page from here.
            onDone(step);
        } catch (_) {
            setMessage({ ok: false, text: SAVE_ERROR });
            setSaving(false);
        }
    };

    const changeUrl = (value) => {
        setUrl(value);
        setPassed(false);
        setMessage(null);
    };

    return (
        <section aria-label="Connect your site">
            <InstallGuidePanel app_url={app_url} onEmbedReady={setEmbedHtml} defaultUrl={defaultUrl} autoScan onUrlChange={changeUrl} />

            <div style={{ marginTop: 24 }}>
                <p style={labelStyle}>Added the snippet? Test it on your landing page:</p>
                <button
                    type="button"
                    className="btn btn-outline-primary"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={testConnection}
                    disabled={!embedHtml || testing || saving}
                >
                    {testing ? 'Testing…' : 'Test Connection'}
                </button>
                {message && (
                    <p role={message.ok ? 'status' : 'alert'} className={message.ok ? 'text-success mt-2' : 'text-danger mt-2'}>
                        {message.text}
                    </p>
                )}
                {/* What the page needs so personalisation holds (Phase 3): before a test, what it already
                    has and what our team decided; once a test passes, the new recommendation. */}
                {url.trim() && <SiteExtensionsPanel app_url={app_url} landingPage={url.trim()} testPassed={passed} />}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 28 }}>
                <button type="button" style={skipStyle} onClick={() => save(false)} disabled={saving || testing}>
                    Skip for now
                </button>
                <button type="button" style={continueStyle(passed)} onClick={() => save(true)} disabled={!passed || saving || testing}>
                    Continue →
                </button>
            </div>
        </section>
    );
};

const labelStyle = { fontSize: 14, fontWeight: 500, color: '#344054', marginBottom: 6, display: 'block' };
const skipStyle = { background: 'none', border: 'none', color: '#667085', fontSize: 14, cursor: 'pointer', textDecoration: 'underline', padding: 0 };
const continueStyle = (enabled) => ({
    backgroundColor: enabled ? PRIMARY : '#e5e7eb',
    border: `1px solid ${enabled ? PRIMARY : '#e5e7eb'}`,
    color: enabled ? 'white' : '#9ca3af',
    padding: '10px 32px',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 14,
    cursor: enabled ? 'pointer' : 'not-allowed',
});

export default ConnectSiteStep;
